import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Card, Form, InputNumber, Popconfirm, Select, Space, Tag, Typography,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useSaveMixComponents } from '../hooks';
import * as fmt from '../format';

const { Text, Paragraph } = Typography;

const MixPackEditor = ({ mix, beerOptions, canEdit }) => {
  const [form] = Form.useForm();
  const saveMix = useSaveMixComponents();

  useEffect(() => {
    form.setFieldsValue({
      components: mix.components.map((c) => ({
        componentBeerName: c.componentBeerName,
        sharePct: Math.round(c.share * 1000) / 10,
      })),
    });
  }, [mix, form]);

  const components = Form.useWatch('components', form) || [];
  const total = components.reduce((sum, c) => sum + (Number(c?.sharePct) || 0), 0);

  const save = async () => {
    const values = await form.validateFields();
    await saveMix.mutateAsync({
      mixBeerName: mix.beerName,
      components: (values.components || []).map((c) => ({
        componentBeerName: c.componentBeerName,
        share: c.sharePct / 100,
      })),
    });
  };

  return (
    <div className="forecasting-section">
      <Space wrap style={{ marginBottom: 8 }}>
        <Text strong>{mix.beerName}</Text>
        {mix.decomposed ? <Tag color="green">Split into components</Tag> : <Tag>Planned as its own line</Tag>}
        <Text type="secondary">
          {`${fmt.litres(mix.demand8wLitres)} demand over 8 weeks · ${fmt.litres(mix.stockLitres)} in stock`}
        </Text>
      </Space>
      <Form form={form} layout="inline" disabled={!canEdit}>
        <Form.List name="components">
          {(fields, { add, remove }) => (
            <Space direction="vertical">
              {fields.map((field) => (
                <Space key={field.key} align="baseline">
                  <Form.Item
                    {...field}
                    name={[field.name, 'componentBeerName']}
                    rules={[{ required: true, message: 'Beer' }]}
                  >
                    <Select showSearch style={{ width: 260 }} options={beerOptions} placeholder="Component beer" />
                  </Form.Item>
                  <Form.Item {...field} name={[field.name, 'sharePct']} rules={[{ required: true, message: 'Share' }]}>
                    <InputNumber min={1} max={100} addonAfter="%" style={{ width: 130 }} />
                  </Form.Item>
                  {canEdit && <MinusCircleOutlined onClick={() => remove(field.name)} />}
                </Space>
              ))}
              {canEdit && (
                <Space>
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>Add beer</Button>
                  <Button type="primary" onClick={save} loading={saveMix.isLoading}>Save</Button>
                  {mix.decomposed && (
                    <Popconfirm
                      title="Stop splitting this pack? It will be planned as its own line."
                      onConfirm={() => saveMix.mutate({
                        mixBeerName: mix.beerName,
                        components: [],
                      })}
                    >
                      <Button>Don&apos;t split</Button>
                    </Popconfirm>
                  )}
                  {components.length > 0 && Math.abs(total - 100) > 0.5 && (
                    <Text type="warning">{`Shares add to ${total}% and will be scaled to 100%`}</Text>
                  )}
                </Space>
              )}
            </Space>
          )}
        </Form.List>
      </Form>
    </div>
  );
};

MixPackEditor.propTypes = {
  mix: PropTypes.shape({
    beerName: PropTypes.string,
    decomposed: PropTypes.bool,
    components: PropTypes.arrayOf(PropTypes.object),
    demand8wLitres: PropTypes.number,
    stockLitres: PropTypes.number,
  }).isRequired,
  beerOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  canEdit: PropTypes.bool.isRequired,
};

const MixPacks = ({ plan, canEdit }) => {
  const beerOptions = plan.beers
    .filter((b) => !b.isMixedPack)
    .map((b) => ({ value: b.beerName, label: b.beerName }));
  return (
    <Card className="forecasting-section" title="Mixed packs">
      <Paragraph type="secondary">
        Cin7 has no bill of materials for the mixed packs, so their make-up was read from the Cin7
        product description. Each pack&apos;s demand and stock is shared across its component
        beers by these percentages, and brew recommendations include the cans needed for mix packs.
      </Paragraph>
      {plan.mixPacks.map((mix) => (
        <MixPackEditor key={mix.beerName} mix={mix} beerOptions={beerOptions} canEdit={canEdit} />
      ))}
    </Card>
  );
};

MixPacks.propTypes = {
  plan: PropTypes.shape({
    beers: PropTypes.arrayOf(PropTypes.object),
    mixPacks: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default MixPacks;
